import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { compose, lifecycle, withStateHandlers } from 'recompose';
import throttle from 'lodash/throttle';
import { addRefComponent, withSubscribeEvent } from '../helpers';
import TextPicker from './TextPicker';
import PlayerPicker from './PlayerPicker';
import TeamPicker from './TeamPicker';
import IconClose from 'material-ui/svg-icons/navigation/close';
import IconButton from 'material-ui/IconButton';

const Dialog = styled.div`
    position: fixed;
    width: 300px;
    z-index: 10;
    transform: translateX(-50%);

    padding: 4px 14px 15px;

    color: rgb(255, 255, 255);
    background-color: rgb(48, 48, 48);
    opacity: ${({ open = false }) => String(open & 1)};
    box-shadow: rgba(0, 0, 0, 0.16) 0 3px 10px, rgba(0, 0, 0, 0.23) 0 3px 10px;
    border-radius: 2px;

    transition: ${({ open = false }) =>
        open
            ? 'opacity 0.1s 0.2s, top 0.35s cubic-bezier(0.23, 1, 0.32, 1) 0s, left 0.35s cubic-bezier(0.23, 1, 0.32, 1) 0s'
            : 'opacity 0.1s, top 0.35s 0.2s cubic-bezier(0.23, 1, 0.32, 1), left 0.35s 0.2s cubic-bezier(0.23, 1, 0.32, 1)'};
`;

const Title = styled.h5`
    margin: -4px 0 16px 8px;
`;

const Content = styled.div`
    > *:not(:last-child) {
        margin-bottom: 23px;
    }
`;

const getCoordinatesWithZoom = (coordinates = 0, zoom = 1) => coordinates - coordinates * (1 - zoom);

const getPositionEditAria = (id = '', zoom = 1) => {
    const element = document.querySelector(`[data-edit-aria-id=${id}]`);
    const coordinates = element != null ? element.getBoundingClientRect() : { top: 0, left: 0, height: 0, width: 0 };

    return {
        top: getCoordinatesWithZoom(coordinates.top, zoom),
        left: getCoordinatesWithZoom(coordinates.left, zoom),
        width: getCoordinatesWithZoom(coordinates.width, zoom),
        height: getCoordinatesWithZoom(coordinates.height, zoom),
    };
};

const handlePositionUpdate = (element, id = '', zoom = 1) => {
    const positionEditAria = getPositionEditAria(id, zoom);
    const windowHeight = document.documentElement.clientHeight;
    const elementPosition = element != null ? element.getBoundingClientRect() : { height: 0 };

    if (element == null) {
        return;
    }

    if (windowHeight < positionEditAria.top + elementPosition.height + 10) {
        element.style.top = positionEditAria.top - elementPosition.height;
    } else {
        element.style.top = positionEditAria.top + positionEditAria.height;
    }

    element.style.left = positionEditAria.left + positionEditAria.width / 2;
};

const iconButtonStyle = {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 30,
    height: 30,
    padding: 0,
};

const PickersControlsDialog = ({ dialogName = '', isOpenDialog = false, handleClose, setRef, ...props }) => (
    <Dialog innerRef={setRef} open={isOpenDialog}>
        {dialogName && <Title>{dialogName}</Title>}
        <IconButton onClick={handleClose} style={iconButtonStyle}>
            <IconClose />
        </IconButton>
        <Content>
            <TextPicker {...props} />
            <PlayerPicker {...props} />
            <TeamPicker {...props} />
        </Content>
    </Dialog>
);

PickersControlsDialog.propTypes = {
    id: PropTypes.string,
    dialogName: PropTypes.string,
    isOpenDialog: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    setRef: PropTypes.func.isRequired,
};

export default compose(
    addRefComponent(),
    withSubscribeEvent('WDP_TEXTM'),
    connect(({ editor = {} }) => ({
        zoom: editor.zoom,
    })),
    withStateHandlers(
        ({ id }) => ({
            isOpenDialog: Boolean(id),
        }),
        {
            handleOpen: () => () => ({ isOpenDialog: true }),
            handleClose: (state, { id, updateWidgetJSPropField = () => {}, enterEditMode = () => {} }) => () => {
                updateWidgetJSPropField(id, { active: false });
                enterEditMode(null);

                return { isOpenDialog: false };
            },
        },
    ),
    lifecycle({
        componentDidMount() {
            this._resizeHandlePositionUpdate = throttle(
                () => handlePositionUpdate(this.props.getRef(), this.props.id, this.props.zoom),
                100,
            );

            window.addEventListener('resize', this._resizeHandlePositionUpdate);
        },
        componentDidUpdate() {
            handlePositionUpdate(this.props.getRef(), this.props.id, this.props.zoom);
        },
        componentWillReceiveProps({ isOpenDialog, id }) {
            if (!isOpenDialog && (!this.props.id && id)) {
                this.props.handleOpen();
            }

            if (isOpenDialog && id == null) {
                this.props.handleClose();
            }
        },
        componentWillUnmount() {
            window.removeEventListener('resize', this._resizeHandlePositionUpdate);
        },
    }),
)(PickersControlsDialog);



/** WEBPACK FOOTER **
 ** ../src/dash/front/editor/pickers/components/PickersControlsDialog.js
 **/